<div class="upload-large-file-wrapper"
     [class.drag-over]="isDragOver"
     [class.uploading]="isUploading"
     [class.file-ready]="fileReady"
     [class.button-hidden]="hidden"
     (dragover)="onDragOver($event)"
     (dragleave)="onDragLeave($event)"
     (drop)="onDrop($event)"
     (click)="!isUploading && !fileReady && !uploadProgress && fileInput.click()">

  <!-- File Input (hidden) -->
  <input #fileInput
         class="hidden"
         [accept]="acceptingMimeTypes"
         type="file"
         (change)="onFileSelected($event.target)">

  <!-- Upload Button (when no file selected) -->
  <div *ngIf="!uploadProgress && !fileReady" class="upload-area">
    <app-button *ngIf="!link"
                [outline]="outline"
                type="button"
                class="add-button"
                [title]="message"
                icon="upload">
      {{ fileName || showImageUrl ? 'Changer de fichier' : (update ? 'Modifier votre fichier' : 'Choisir votre fichier') }}
    </app-button>

    <a *ngIf="link">
      <ng-content></ng-content>
    </a>

    <div class="upload-hint">
      <p>Glissez-déposez votre fichier ici ou cliquez pour sélectionner</p>
      <p class="file-size-hint">Fichiers jusqu'à 2.5 GB supportés</p>
    </div>

    <div class="text">
      {{ showFileName && !!fileName?.length ? fileName : (showMessage ? message : '') }}
    </div>
  </div>

  <!-- File Ready Area (file selected but not uploading yet) -->
  <div *ngIf="fileReady && !uploadProgress" class="file-ready-container">
    <div class="file-ready-header">
      <div class="file-info-section">
        <div class="file-icon">
          <app-icon type="file" size="24" color="#6366f1"></app-icon>
        </div>
        <div class="file-meta">
          <div class="file-name-display">{{ fileName }}</div>
          <div class="file-size-display" *ngIf="fileInput.files?.[0]">
            {{ formatFileSize(fileInput.files![0].size) }}
          </div>
        </div>
      </div>

      <button class="remove-file-button"
              (click)="cancelUpload(); $event.stopPropagation()"
              title="Supprimer le fichier"
              type="button">
        <app-icon type="close" size="18"></app-icon>
      </button>
    </div>

    <div class="file-status-section">
      <div class="status-indicator">
        <app-icon type="check" size="16" color="#10b981"></app-icon>
        <span class="status-text">Fichier prêt - Upload au clic sur "Enregistrer"</span>
      </div>
    </div>

    <div class="file-actions-section">
      <app-button type="button"
                  [outline]="true"
                  icon="upload"
                  (click)="click(); $event.stopPropagation()">
        Changer de fichier
      </app-button>
    </div>
  </div>

  <!-- Upload Progress Area -->
  <div *ngIf="uploadProgress" class="upload-progress-area">
    <div class="file-info">
      <div class="file-name">{{ uploadProgress.fileName }}</div>
      <div class="file-status" [ngClass]="uploadProgress.status">
        <span *ngIf="uploadProgress.status === 'preparing'">Préparation...</span>
        <span *ngIf="uploadProgress.status === 'uploading'">
          {{ uploadProgress.statusMessage || 'Upload en cours...' }} {{ uploadProgress.progress }}%
        </span>
        <span *ngIf="uploadProgress.status === 'processing'">
          <app-spinner *ngIf="uploadProgress.progress >= 85" size="tiny" style="display: inline-block; margin-right: 4px;"></app-spinner>
          {{ uploadProgress.statusMessage || 'Traitement...' }}
        </span>
        <span *ngIf="uploadProgress.status === 'finalizing'">
          <app-spinner size="tiny" style="display: inline-block; margin-right: 4px;"></app-spinner>
          {{ uploadProgress.statusMessage || 'Finalisation...' }}
        </span>
        <span *ngIf="uploadProgress.status === 'paused'">Upload en pause</span>
        <span *ngIf="uploadProgress.status === 'completed'">✓ Upload terminé</span>
        <span *ngIf="uploadProgress.status === 'error'">❌ {{ uploadProgress.error }}</span>
      </div>
    </div>

    <!-- Progress Bar -->
    <div class="progress-container" *ngIf="uploadProgress.status !== 'completed'">
      <div class="progress-bar">
        <div class="progress-fill"
             [style.width.%]="uploadProgress.progress"
             [ngClass]="{
               'uploading': uploadProgress.status === 'uploading',
               'processing': uploadProgress.status === 'processing' || uploadProgress.status === 'finalizing',
               'paused': uploadProgress.status === 'paused',
               'error': uploadProgress.status === 'error'
             }">
        </div>
      </div>
      <div class="progress-text">
        {{ uploadProgress.progress }}%
        <span *ngIf="uploadProgress.stage === 'processing' || uploadProgress.stage === 'finalizing'" class="stage-indicator">
          ({{ uploadProgress.stage === 'processing' ? 'Traitement' : 'Finalisation' }})
        </span>
      </div>
    </div>

    <!-- Upload Statistics -->
    <div class="upload-stats" *ngIf="uploadProgress.status === 'uploading' && (uploadProgress.loaded || uploadProgress.uploadSpeed || uploadProgress.eta)">
      <div class="stat-item" *ngIf="uploadProgress.loaded && uploadProgress.total">
        <span class="stat-label">Taille:</span>
        <span class="stat-value">{{ formatFileSize(uploadProgress.loaded) }} / {{ formatFileSize(uploadProgress.total) }}</span>
      </div>
      <div class="stat-item" *ngIf="uploadProgress.uploadSpeed">
        <span class="stat-label">Vitesse:</span>
        <span class="stat-value">{{ formatSpeed(uploadProgress.uploadSpeed) }}</span>
      </div>
      <div class="stat-item" *ngIf="uploadProgress.eta && uploadProgress.progress < 85">
        <span class="stat-label">Temps restant:</span>
        <span class="stat-value">~{{ formatETA(uploadProgress.eta) }}</span>
      </div>
    </div>

    <!-- Processing Indicator -->
    <div class="processing-indicator" *ngIf="uploadProgress.status === 'processing' || uploadProgress.status === 'finalizing'">
      <p class="processing-message">
        Le fichier a été envoyé avec succès. Traitement en cours sur le serveur...
      </p>
    </div>

    <!-- Error retry button -->
    <div class="upload-controls" *ngIf="uploadProgress.status === 'error'">
      <button class="control-btn retry-btn" (click)="retryUpload(); $event.stopPropagation()">
        🔄 Réessayer
      </button>
      <button class="control-btn cancel-btn" (click)="cancelUpload(); $event.stopPropagation()">
        <app-icon type="close" size="14"></app-icon>
        Annuler
      </button>
    </div>
  </div>
</div>